import { generateSlideshowVideo, SlideshowResult, PhotoWithRoomType } from './generateSlideshowVideo';
import { generateListingFlyer, FlyerResult } from './generateListingFlyer';
import { RoomType, DescriptionLanguage } from '@/types/property';
import { supabase } from '@/integrations/supabase/client';

/**
 * Track video generation usage in the database
 * Called after successful video generation to update usage counters
 */
export async function trackVideoUsage(): Promise<void> {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      console.warn('No user ID available for video usage tracking');
      return;
    }
    
    console.log(`📊 Tracking video generation usage for user ${userId}`);
    
    // Server-side counter — enforces plan limits and prevents client tampering.
    const { data, error } = await supabase.rpc('increment_monthly_usage', {
      p_user_id: userId,
      p_service_type: 'VIDEO_GENERATION',
      p_count: 1,
    });

    if (error) {
      console.error('Failed to track video usage:', error);
    } else if (data && data.length > 0) {
      console.log(`✅ Tracked video usage. New total: ${data[0].new_total}/${data[0].monthly_limit}`);
    }
  } catch (err) {
    console.error('Error tracking video usage:', err);
  }
}
export interface ProfilePictureResult {
  success: boolean;
  profilePicture?: string;
  blob?: Blob;
  error?: string;
}

// New flyer result type (replaces collage)
export interface ListingFlyerResult {
  success: boolean;
  pdfUrl?: string;
  imagePreviewUrl?: string;  // Image preview for display
  blob?: Blob;
  imageBlob?: Blob;  // Image blob for preview upload
  error?: string;
}

export interface VideoResult {
  success: boolean;
  videoUrl?: string;
  blob?: Blob;
  duration?: number;
  error?: string;
}

export interface PropertyCollageInfo {
  propertyType?: string;
  city?: string;
  price?: string;
  bedrooms?: number;
  bathrooms?: number;
  squareMeters?: string;
  listingType?: 'sale' | 'rent';
  address?: string;
  features?: string[];
  description?: string;
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  agencyName?: string;
  language?: DescriptionLanguage;  // Language for flyer text
  currency?: string;               // ISO currency (USD/AED/EUR…)
  areaUnit?: 'sqm' | 'sqft';       // Display unit for size
}

export interface PhotoWithRoom {
  url: string;
  roomType?: RoomType;
}

/**
 * Generate a professional PDF listing flyer from enhanced photos with listing information
 * This replaces the old photo collage with a more valuable marketing asset
 * 
 * @deprecated Use generatePropertyListingFlyer for new implementations
 */
export async function generatePropertyProfilePicture(
  enhancedPhotoUrls: string[],
  propertyType?: string,
  city?: string,
  style: 'modern' | 'elegant' | 'vibrant' | 'minimal' = 'modern',
  propertyInfo?: PropertyCollageInfo
): Promise<ListingFlyerResult> {
  try {
    console.log('📄 Generating property listing flyer (PDF)...');

    if (!enhancedPhotoUrls || enhancedPhotoUrls.length === 0) {
      return { success: false, error: 'No photos provided' };
    }

    const result: FlyerResult = await generateListingFlyer({
      photos: enhancedPhotoUrls,
      propertyType: propertyInfo?.propertyType || propertyType,
      city: propertyInfo?.city || city,
      address: propertyInfo?.address,
      price: propertyInfo?.price,
      bedrooms: propertyInfo?.bedrooms,
      bathrooms: propertyInfo?.bathrooms,
      squareMeters: propertyInfo?.squareMeters,
      listingType: propertyInfo?.listingType,
      features: propertyInfo?.features,
      description: propertyInfo?.description,
      agentName: propertyInfo?.agentName,
      agentPhone: propertyInfo?.agentPhone,
      agentEmail: propertyInfo?.agentEmail,
      agencyName: propertyInfo?.agencyName,
      language: propertyInfo?.language,
      currency: propertyInfo?.currency,
      areaUnit: propertyInfo?.areaUnit,
    });

    if (result.success && result.pdfUrl) {
      console.log('✅ Property listing flyer (PDF) generated successfully');
      return { 
        success: true, 
        pdfUrl: result.pdfUrl, 
        imagePreviewUrl: result.imagePreviewUrl,
        blob: result.blob,
        imageBlob: result.imageBlob 
      };
    }

    return { success: false, error: result.error || 'Flyer generation failed' };
  } catch (err) {
    console.error('Property listing flyer generation failed:', err);
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'Unknown error' 
    };
  }
}

/**
 * Generate a professional PDF listing flyer from enhanced photos with listing information
 * Creates a downloadable marketing asset with property photos and details
 */
export async function generatePropertyListingFlyer(
  enhancedPhotoUrls: string[],
  propertyInfo: PropertyCollageInfo
): Promise<ListingFlyerResult> {
  try {
    console.log('📄 Generating property listing flyer (PDF)...');

    if (!enhancedPhotoUrls || enhancedPhotoUrls.length === 0) {
      return { success: false, error: 'No photos provided' };
    }

    const result: FlyerResult = await generateListingFlyer({
      photos: enhancedPhotoUrls,
      propertyType: propertyInfo.propertyType,
      city: propertyInfo.city,
      address: propertyInfo.address,
      price: propertyInfo.price,
      bedrooms: propertyInfo.bedrooms,
      bathrooms: propertyInfo.bathrooms,
      squareMeters: propertyInfo.squareMeters,
      listingType: propertyInfo.listingType,
      features: propertyInfo.features,
      description: propertyInfo.description,
      agentName: propertyInfo.agentName,
      agentPhone: propertyInfo.agentPhone,
      agentEmail: propertyInfo.agentEmail,
      agencyName: propertyInfo.agencyName,
      language: propertyInfo.language,
      currency: propertyInfo.currency,
      areaUnit: propertyInfo.areaUnit,
    });

    if (result.success && result.pdfUrl) {
      console.log('✅ Property listing flyer (PDF) generated successfully');
      return { 
        success: true, 
        pdfUrl: result.pdfUrl, 
        imagePreviewUrl: result.imagePreviewUrl,
        blob: result.blob,
        imageBlob: result.imageBlob 
      };
    }

    return { success: false, error: result.error || 'Flyer generation failed' };
  } catch (err) {
    console.error('Property listing flyer generation failed:', err);
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'Unknown error' 
    };
  }
}

/**
 * Generate a slideshow video for the property using enhanced photos
 * Uses client-side Canvas + MediaRecorder with Ken Burns effects and smooth transitions
 * Photos are automatically sorted by room type for professional flow:
 * Exterior First -> Living Room -> Kitchen -> Dining -> Bedrooms -> Bathrooms -> Other -> Exterior Last
 * 
 * Each photo displays for 6 seconds with smooth 1.2s crossfade transitions
 */
export async function generatePropertyVideo(
  enhancedPhotos: string[] | PhotoWithRoom[],
  propertyType?: string,
  city?: string,
  secondsPerPhoto: number = 5, // Match core engine default
  style: 'cinematic' | 'smooth' | 'dynamic' = 'cinematic'
): Promise<VideoResult> {
  try {
    console.log('🎬 Generating property slideshow video...');

    if (!enhancedPhotos || enhancedPhotos.length === 0) {
      return { success: false, error: 'No photos provided' };
    }

    // Convert to PhotoWithRoomType format for sorting
    const photosWithRoomTypes: PhotoWithRoomType[] = enhancedPhotos.map(photo => {
      if (typeof photo === 'string') {
        return { url: photo, roomType: 'other' as RoomType };
      }
      return { url: photo.url, roomType: photo.roomType || 'other' };
    });

    // Transition duration based on style: cinematic gets 1s, smooth gets 1.5s, dynamic gets 0.6s
    const transitionSecs = style === 'dynamic' ? 0.6 : style === 'smooth' ? 1.5 : 1;
    
    console.log(`📹 Video settings: ${photosWithRoomTypes.length} photos × ${secondsPerPhoto}s each, ${transitionSecs}s transitions`);

    const result: SlideshowResult = await generateSlideshowVideo({
      photos: photosWithRoomTypes,
      photoDuration: secondsPerPhoto,
      transitionDuration: transitionSecs,
      width: 1280,
      height: 720,
      fps: 30,
    });

    if (result.success && result.videoUrl) {
      console.log('✅ Property slideshow video generated successfully');
      
      // NOTE: Video usage tracking is now handled by the caller (useListingCreation)
      // after the entire listing creation process completes successfully.
      
      return { 
        success: true, 
        videoUrl: result.videoUrl,
        blob: result.blob,
        duration: photosWithRoomTypes.length * secondsPerPhoto
      };
    }

    return { success: false, error: result.error || 'Video generation failed' };
  } catch (err) {
    console.error('Video generation failed:', err);
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'Unknown error' 
    };
  }
}
